import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
export const getBackendUrl = () => BACKEND_URL;

/* v3.2.2 · Per-tab session support.

   Each browser tab can hold its OWN authentication token via
   `sessionStorage.eengdes_token` — sessionStorage is scoped per-tab so
   two tabs on the same domain can be signed in as different users at
   the same time (fixes the "one account per device" trap).

   Auth priority (server-side is the same order):
     1. `Authorization: Bearer <token>` header   ← per-tab, if present
     2. `access_token` HttpOnly cookie           ← legacy shared session

   When a per-tab token is present we DISABLE `withCredentials` on the
   request so the shared cookie isn't sent alongside — otherwise the
   server would still see the shared cookie and, if it were to change
   its priority, cross-tab bleed could return. */

export const TAB_TOKEN_KEY = "eengdes_token";
export const getTabToken = () => {
  try { return sessionStorage.getItem(TAB_TOKEN_KEY) || null; } catch { return null; }
};
export const setTabToken = (t) => {
  try { if (t) sessionStorage.setItem(TAB_TOKEN_KEY, t);
        else   sessionStorage.removeItem(TAB_TOKEN_KEY); } catch { /* ignore */ }
};

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  const tok = getTabToken();
  if (tok) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${tok}`;
    // Skip cookie for per-tab requests so the two auths never mix.
    config.withCredentials = false;
  }
  return config;
});

/* v3.2.50 · Global 401 → hard sign-out. When a deleted / banned user
   still has a valid-looking JWT cookie, the very next API call comes
   back with `401 · Account deleted` (see auth.py get_current_user).
   We catch that here, wipe the per-tab token, and hard-reload so the
   AuthContext's /auth/me check flips the app back to the guest view.
   Login / register / refresh / forgot-password 401s are LEFT ALONE
   because those are ordinary "bad credentials" responses that the
   caller displays inline. */
const _AUTH_SILENT_PATHS = [
  "/auth/login", "/auth/register", "/auth/refresh",
  "/auth/forgot-password", "/auth/reset-password",
  "/auth/me",
];
let _kicked = false;
api.interceptors.response.use(
  (r) => r,
  async (err) => {
    try {
      const status = err.response?.status;
      const url = err.config?.url || "";
      const silent = _AUTH_SILENT_PATHS.some((p) => url.includes(p));
      if (status === 401 && !silent && !_kicked) {
        _kicked = true;
        try { sessionStorage.removeItem(TAB_TOKEN_KEY); } catch { /* ignore */ }
        const { toast } = await import("sonner");
        toast.error("Your session ended. Please sign in again.");
        // Small delay so the toast is visible before the reload.
        setTimeout(() => { window.location.href = "/"; }, 900);
      }
    } catch { /* never crash callers */ }
    return Promise.reject(err);
  }
);

/* v3.2.41 · Auto-retry send on email-verification 403.
   When any authenticated action fails with a 403 because the user
   hasn't verified their email, surface a toast with a one-tap resend
   action so they don't have to hunt for the banner. */
let _verifyResendInFlight = false;
api.interceptors.response.use(
  (r) => r,
  async (err) => {
    try {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "";
      if (status === 403 && /verify\s+your\s+email/i.test(msg) && !_verifyResendInFlight) {
        _verifyResendInFlight = true;
        // Lazy imports so we don't create a module-load cycle.
        const { toast } = await import("sonner");
        toast.error(msg, {
          duration: 8000,
          action: {
            label: "Resend email",
            onClick: async () => {
              try {
                const { data } = await api.post("/auth/resend-verify");
                if (data.already_verified) {
                  toast.success("Already verified — refresh to unlock.");
                } else if (data.ok) {
                  toast.success("Verification email sent — check your inbox.");
                } else {
                  toast.info("Verification email service is temporarily unavailable.");
                }
              } catch (e) {
                const d = e.response?.data?.detail;
                toast.error(typeof d === "string" ? d : "Resend failed");
              }
            },
          },
        });
        // Reset flag on next microtask so future 403s can toast again.
        setTimeout(() => { _verifyResendInFlight = false; }, 1000);
      }
    } catch { /* never let the interceptor crash callers */ }
    return Promise.reject(err);
  }
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  // Structured errors from the backend (e.g. {code, message, …}). Prefer the
  // human-readable `message` when present.
  if (detail && typeof detail === "object" && typeof detail.message === "string")
    return detail.message;
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
